import { useState, useEffect, useCallback } from "react";
import BookingsList from "../booking/bookingsList";
import { getAllBookings } from "../../api/booking-api";
import { useLanguage } from "../../providers/preferenceProvider";
import Overlay from "../overlays/editBooking";
import { useSearchParams } from "react-router-dom";
import SearchComponent from "../searchComponent";
import FiltersList from "../filters/filtersList";
import { getFiltersBookingsAdminPage } from "../../api/filter-api";
import { ACL } from "../../acls";
import { useConfig } from "../../providers/configProvider";

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState({});
  const [showOverlay, setShowOverlay] = useState(false);
  const [count, setCount] = useState(0);
  const [pageSize] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const { t } = useLanguage();
  const { isAuthorized, config } = useConfig();

  // Filters
  const [filtersList, setFiltersList] = useState([]);
  const [clear, setClear] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  
  useEffect(() => {
    const getFilters = async () => {
      setFiltersList((await getFiltersBookingsAdminPage()).data);
    };
    
    getFilters();
    searchParams.set('pageSize', pageSize);
    setSearchParams(searchParams);
  }, []);

  useEffect(() => {
    fetchBookings(true);
  }, [searchParams]);

  const fetchBookings = useCallback(async (refresh = false) => {
    const queryParams = new URLSearchParams();
    queryParams.append('sort', 'endTime:DESC');
    queryParams.append('search', searchParams.get('bookings_search'));
    queryParams.append('pageSize', searchParams.get('pageSize') || pageSize);
    queryParams.append('page', refresh ? 1 : Math.ceil(bookings.length / searchParams.get('pageSize') || 25) + 1);
    queryParams.append('rooms', true);
    if (searchParams.get('filters'))
      appendQueryParamsFromSelected(searchParams.get('filters'), queryParams);

    await getAllBookings(queryParams).then(response => {
      const newBookings = response.data.rows || [];
      setBookings(prev =>
        refresh ? newBookings : [...prev, ...newBookings]
      );
      setHasMore(((refresh ? 0 : bookings.length) + newBookings.length) < response.data.count);
      setCount(response.data.count);
    }).catch(error => {
      console.error("Error fetching bookings:", error);
    });
  }, [searchParams, bookings.length]);

  function appendQueryParamsFromSelected(filters, queryParams) {
    const filterObject = JSON.parse(filters);
    for (const [name, items] of Object.entries(filterObject)) {
      items.forEach(item => {
        queryParams.append(name, item);
      });
    }
  }

  function openOverlay(booking) {
    booking.startTime = new Date(booking.startTime);
    booking.endTime = new Date(booking.endTime);
    setSelectedBooking(booking);
    setShowOverlay(true);
  }

  function closeOverlay() {
    setShowOverlay(false);
    fetchBookings(true);
  }

  function handleSearch(search) {
    searchParams.set('bookings_search', search);
    setSearchParams(searchParams);
  }

  function selectedListChanged(filters){
    let filteredFilters = { ...filters };
    Object.keys(filters).filter(key => filters[key].length < 1).forEach(key => {
      delete filteredFilters[key];
    })

    mapFiltersToKeys(filteredFilters);
    searchParams.set('filters', JSON.stringify(filteredFilters));
    setSearchParams(searchParams);
  }

  function mapFiltersToKeys(filteredFilters) {
    Object.keys(filteredFilters).forEach(key => {
      filteredFilters[key] = filteredFilters[key].map(item => item.key);
    });
  }

  function isBookingOwner(booking) {
    if (booking && booking?.Users) {
      return booking.Users.some(user => user.id === config.user.id && user.UserBooking.creator);
    }
  }

  return (
    <>
      <div className="admin-list-wrapper">
        <div className="admin-list-header mb-4 flex justify-between items-center">
          <div>{t.amount_of + " " + t.bookings + ": " + count}</div>
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <div className="flex flex-row items-center gap-4">
            <SearchComponent onSearchChange={handleSearch}></SearchComponent>
          </div>
          <div className="flex flex-wrap">
            <FiltersList listFilters={filtersList} onSelectedChange={x => selectedListChanged(x)} clear={clear}/>
          </div>
          <button className='btn px-4 py-2' onClick={() => setClear(x => x + 1)}>
            {t.clear}
          </button>
        </div>
        <div className="admin-list-box mt-4">
          {bookings.length > 0 && (
            <>
              <BookingsList bookingsList={bookings} elementColor="var(--white-color)" width={100} onBookingClick={openOverlay} />
              <div className="grid grid-cols-4 px-4 py-2">
                <div>
                  <h2>{t.showing.replace("{ph_length}", bookings.length).replace("{ph_count}", count)}</h2>
                  {hasMore && (
                    <button className="admin-edit-button mt-2" onClick={() => fetchBookings()}>
                      {t.show_more}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Overlay
        title={(new Date(selectedBooking.endTime) > new Date()) ? t.manage_booking : 'Booking details'}
        isOpen={showOverlay}
        onClose={closeOverlay}
        room={selectedBooking.Room}
        inputBooking={selectedBooking}
        editable={(isAuthorized(ACL.BOOKING) || isBookingOwner(selectedBooking)) && (new Date(selectedBooking.endTime) > new Date())}
        newBooking={false}
      />
    </>
  );
};

export default Bookings;
